import React, { useState, useEffect } from 'react';
import PointService, { PointRule, PointCategory } from '../services/PointService';

interface PointItem {
  id?: string;
  points: string;
  description: string;
  criteria: string;
  frequency: string;
  enabled: boolean;
  trackable: boolean;
  weeklyCap?: number;
  implementationNotes?: string;
}

interface Category {
  id?: string;
  category: string;
  points: PointItem[];
  enabled: boolean;
}

const LeaderboardPoints: React.FC = () => {
  const [pointsData, setPointsData] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pointService = PointService.getInstance();

  // Frequency options for dropdown
  const frequencyOptions = [
    'Daily',
    'Weekly', 
    'Weekly (capped)',
    'Per session',
    'Per lesson',
    'Per task',
    'Per badge',
    'Per record',
    'Ongoing'
  ];

  const [editingItem, setEditingItem] = useState<{categoryIndex: number, pointIndex: number} | null>(null);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Load data from PointService
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await pointService.initialize();
        
        const categories = pointService.getCategories();
        const rules = pointService.getRules();
        
        // Group rules by category
        const groupedData: Category[] = categories.map(category => ({
          id: category.id,
          category: category.name,
          enabled: category.enabled,
          points: rules
            .filter(rule => rule.category === category.name)
            .map(rule => ({
              id: rule.id,
              points: rule.points,
              description: rule.description,
              criteria: rule.criteria,
              frequency: rule.frequency,
              enabled: rule.enabled,
              trackable: rule.trackable,
              weeklyCap: rule.weeklyCap,
              implementationNotes: rule.implementationNotes
            }))
            .sort((a, b) => {
              const ruleA = rules.find(r => r.id === a.id);
              const ruleB = rules.find(r => r.id === b.id);
              return (ruleA?.order || 0) - (ruleB?.order || 0);
            })
        }));
        
        setPointsData(groupedData);
        setError(null);
      } catch (err) {
        console.error('Error loading point data:', err);
        setError('Failed to load point system data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Edit point item
  const handleEditPoint = async (categoryIndex: number, pointIndex: number, field: keyof PointItem, value: string | boolean | number) => {
    const point = pointsData[categoryIndex].points[pointIndex];
    if (!point.id) return;

    try {
      await pointService.updateRule(point.id, { [field]: value });
      
      // Update local state
      const newData = [...pointsData];
      (newData[categoryIndex].points[pointIndex] as any)[field] = value;
      setPointsData(newData);
    } catch (error) {
      console.error('Error updating point:', error);
      setError('Failed to update point rule');
    }
  };

  // Edit category name
  const handleEditCategory = async (categoryIndex: number, newName: string) => {
    const category = pointsData[categoryIndex];
    if (!category.id) return;

    try {
      await pointService.updateCategory(category.id, { name: newName });
      
      // Update local state
      const newData = [...pointsData];
      newData[categoryIndex].category = newName;
      setPointsData(newData);
    } catch (error) {
      console.error('Error updating category:', error);
      setError('Failed to update category');
    }
  };

  // Toggle point enabled/disabled
  const handleTogglePoint = async (categoryIndex: number, pointIndex: number) => {
    const point = pointsData[categoryIndex].points[pointIndex];
    if (!point.id) return;

    try {
      await pointService.toggleRule(point.id);
      
      // Update local state
      const newData = [...pointsData];
      newData[categoryIndex].points[pointIndex].enabled = !point.enabled;
      setPointsData(newData);
    } catch (error) {
      console.error('Error toggling point:', error);
      setError('Failed to toggle point rule');
    }
  };

  // Toggle category enabled/disabled
  const handleToggleCategory = async (categoryIndex: number) => {
    const category = pointsData[categoryIndex];
    if (!category.id) return;

    try {
      await pointService.toggleCategory(category.id);
      
      // Update local state
      const newData = [...pointsData];
      newData[categoryIndex].enabled = !category.enabled;
      setPointsData(newData);
    } catch (error) {
      console.error('Error toggling category:', error);
      setError('Failed to toggle category');
    }
  };

  // Add new point to category
  const handleAddPoint = async (categoryIndex: number) => {
    const category = pointsData[categoryIndex];
    
    try {
      const newRule = {
        category: category.category,
        points: '+0',
        description: 'New point item',
        criteria: 'Enter criteria here',
        frequency: 'Daily',
        enabled: true,
        trackable: true,
        order: category.points.length + 1,
        weeklyCap: 1,
        implementationNotes: 'Add implementation guidance here'
      };
      
      const ruleId = await pointService.addRule(newRule);
      
      // Update local state
      const newData = [...pointsData];
      newData[categoryIndex].points.push({
        id: ruleId,
        ...newRule
      });
      setPointsData(newData);
    } catch (error) {
      console.error('Error adding point:', error);
      setError('Failed to add point rule');
    }
  };

  // Delete point from category
  const handleDeletePoint = async (categoryIndex: number, pointIndex: number) => {
    const point = pointsData[categoryIndex].points[pointIndex];
    if (!point.id || !window.confirm('Are you sure you want to delete this point item?')) return;

    try {
      await pointService.deleteRule(point.id);
      
      // Update local state
      const newData = [...pointsData];
      newData[categoryIndex].points.splice(pointIndex, 1);
      setPointsData(newData);
    } catch (error) {
      console.error('Error deleting point:', error);
      setError('Failed to delete point rule');
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const newCategory = {
        name: newCategoryName.trim(),
        enabled: true,
        order: pointsData.length + 1
      };
      
      const categoryId = await pointService.addCategory(newCategory);
      
      // Update local state
      const newData = [...pointsData, {
        id: categoryId,
        category: newCategory.name,
        enabled: newCategory.enabled,
        points: []
      }];
      setPointsData(newData);
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (error) {
      console.error('Error adding category:', error);
      setError('Failed to add category');
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryIndex: number) => {
    const category = pointsData[categoryIndex];
    if (!category.id || !window.confirm('Are you sure you want to delete this entire category? This will remove all points in this category.')) return;

    try {
      await pointService.deleteCategory(category.id);
      
      // Update local state
      const newData = [...pointsData];
      newData.splice(categoryIndex, 1);
      setPointsData(newData);
    } catch (error) {
      console.error('Error deleting category:', error);
      setError('Failed to delete category');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading point system...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Point System</h2>
          <p className="text-lg text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                🏆 Student Leaderboard Point System
              </h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                Earn points for your musical practice and progress! This comprehensive point system rewards 
                consistent practice, skill development, and engagement with your musical journey.
              </p>
              
              {/* Trackability Guide */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-4xl mx-auto">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">📋 Rule Trackability Guide</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <div className="bg-white rounded-lg p-3">
                    <h4 className="font-semibold mb-2 text-green-700">✅ Trackable Rules</h4>
                    <ul className="space-y-1 text-xs">
                      <li>• <strong>Practice sessions</strong> - Automatically tracked</li>
                      <li>• <strong>Badge awards</strong> - System-generated events</li>
                      <li>• <strong>Streak calculations</strong> - Based on daily data</li>
                      <li>• <strong>Custom task creation</strong> - Database events</li>
                      <li>• <strong>Daily/weekly goals</strong> - Time-based calculations</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h4 className="font-semibold mb-2 text-red-700">❌ Not Trackable Rules</h4>
                    <ul className="space-y-1 text-xs">
                      <li>• <strong>Lesson completion</strong> - Requires teacher verification</li>
                      <li>• <strong>Page visits</strong> - Need tracking implementation</li>
                      <li>• <strong>Manual actions</strong> - User-initiated activities</li>
                      <li>• <strong>Time-based records</strong> - Need historical comparison</li>
                      <li>• <strong>External activities</strong> - Outside system scope</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 text-xs text-blue-600">
                  <strong>💡 Tip:</strong> Hover over the Implementation column to see detailed guidance for each rule!
                </div>
              </div>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
              {/* Add Category Button */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowAddCategory(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  + Add New Category
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to reset the entire point system to defaults? This will delete all custom rules and categories.')) {
                      try {
                        await pointService.resetToDefaults();
                        window.location.reload(); // Reload to refresh the data
                      } catch (error) {
                        console.error('Error resetting point system:', error);
                        setError('Failed to reset point system');
                      }
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                  title="Reset entire point system to default values"
                >
                  🔄 Reset to Defaults
                </button>
              </div>
          
          {/* Add Category Form */}
          {showAddCategory && (
            <div className="mt-4 bg-white rounded-lg p-4 shadow-md max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-3">Add New Category</h3>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Enter category name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleAddCategory}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Add Category
                </button>
                <button
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName('');
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Points Table */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criteria for Applying Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Frequency
                  </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Implementation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        Actions
                      </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pointsData.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                        {/* Category Header */}
                        <tr className={!category.enabled ? "bg-gray-100" : "bg-blue-50"}>
                          <td colSpan={7} className="px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {editingCategory === categoryIndex ? (
                              <input
                                type="text"
                                value={category.category}
                                onChange={(e) => handleEditCategory(categoryIndex, e.target.value)}
                                onBlur={() => setEditingCategory(null)}
                                onKeyPress={(e) => e.key === 'Enter' && setEditingCategory(null)}
                                className={`text-lg font-semibold bg-transparent border-b-2 focus:outline-none ${
                                  !category.enabled
                                    ? 'text-gray-500 border-gray-300 focus:border-gray-600'
                                    : 'text-blue-900 border-blue-300 focus:border-blue-600'
                                }`}
                                autoFocus
                              />
                            ) : (
                              <h3 
                                className={`text-lg font-semibold cursor-pointer ${
                                  !category.enabled
                                    ? 'text-gray-500 hover:text-gray-700'
                                    : 'text-blue-900 hover:text-blue-700'
                                }`}
                                onClick={() => setEditingCategory(categoryIndex)}
                              >
                                {category.category}
                              </h3>
                            )}
                            <button
                              onClick={() => handleToggleCategory(categoryIndex)}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                category.enabled
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={category.enabled ? 'Disable Category' : 'Enable Category'}
                            >
                              {category.enabled ? '✓ Enabled' : '✗ Disabled'}
                            </button>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAddPoint(categoryIndex)}
                              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                              title="Add Point to Category"
                            >
                              + Add Point
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(categoryIndex)}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                              title="Delete Category"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Category Points */}
                    {category.points.map((point, pointIndex) => {
                      return (
                        <tr key={pointIndex} className={`hover:bg-gray-50 ${!point.enabled ? 'bg-gray-100' : !point.trackable ? 'bg-red-50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={point.points}
                                onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'points', e.target.value)}
                                className={`w-16 px-2 py-1 text-sm font-semibold rounded border ${
                                  point.points.includes('x') 
                                    ? 'bg-purple-100 text-purple-800 border-purple-300' 
                                    : !point.trackable
                                    ? 'bg-red-100 text-red-800 border-red-300'
                                    : 'bg-green-100 text-green-800 border-green-300'
                                } ${!point.enabled ? 'opacity-50' : ''}`}
                                disabled={!point.enabled}
                              />
                              {!point.trackable && (
                                <span className="text-xs bg-red-200 text-red-800 px-1 py-0.5 rounded">
                                  Not Trackable
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={point.description}
                                onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'description', e.target.value)}
                                className={`w-full px-2 py-1 text-sm font-medium border rounded focus:outline-none ${
                                  !point.trackable
                                    ? 'text-red-900 border-red-300 focus:border-red-500'
                                    : 'text-gray-900 border-gray-300 focus:border-blue-500'
                                } ${!point.enabled ? 'opacity-50' : ''}`}
                                disabled={!point.enabled}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <textarea
                              value={point.criteria}
                              onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'criteria', e.target.value)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none ${
                                !point.trackable
                                  ? 'text-red-600 border-red-300 focus:border-red-500'
                                  : 'text-gray-600 border-gray-300 focus:border-blue-500'
                              } ${!point.enabled ? 'opacity-50' : ''}`}
                              rows={2}
                              disabled={!point.enabled}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <select
                                value={point.frequency}
                                onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'frequency', e.target.value)}
                                className={`w-32 px-2 py-1 text-xs font-medium border rounded focus:outline-none ${
                                  !point.trackable
                                    ? 'bg-red-100 text-red-800 border-red-300 focus:border-red-500'
                                    : 'bg-gray-100 text-gray-800 border-gray-300 focus:border-blue-500'
                                } ${!point.enabled ? 'opacity-50' : ''}`}
                                disabled={!point.enabled}
                              >
                                {frequencyOptions.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                              {point.frequency === 'Weekly (capped)' && (
                                <input
                                  type="number"
                                  value={point.weeklyCap || 1}
                                  onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'weeklyCap', parseInt(e.target.value) || 1)}
                                  min="1"
                                  max="7"
                                  className="w-20 px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  placeholder="Cap"
                                  disabled={!point.enabled}
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleTogglePoint(categoryIndex, pointIndex)}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                point.enabled
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={point.enabled ? 'Disable Point Rule' : 'Enable Point Rule'}
                            >
                              {point.enabled ? '✓' : '✗'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="relative group">
                              <textarea
                                value={point.implementationNotes || ''}
                                onChange={(e) => handleEditPoint(categoryIndex, pointIndex, 'implementationNotes', e.target.value)}
                                className={`w-full px-2 py-1 text-xs border rounded focus:outline-none ${
                                  point.trackable
                                    ? 'bg-green-50 text-green-800 border-green-300 focus:border-green-500'
                                    : 'bg-red-50 text-red-800 border-red-300 focus:border-red-500'
                                } ${!point.enabled ? 'opacity-50' : ''}`}
                                rows={2}
                                disabled={!point.enabled}
                                placeholder={point.trackable ? "Implementation notes for trackable rule" : "Why this rule is not trackable"}
                              />
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 max-w-xs">
                                <div className="font-semibold mb-1">
                                  {point.trackable ? '✅ Trackable Rule' : '❌ Not Trackable'}
                                </div>
                                <div className="text-xs">
                                  {point.trackable 
                                    ? 'This rule can be automatically tracked by the system. Add notes about where/how it\'s implemented.'
                                    : 'This rule requires manual verification or additional code to implement. Explain why it\'s not automatically trackable.'
                                  }
                                </div>
                                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeletePoint(categoryIndex, pointIndex)}
                              className="text-red-500 hover:text-red-700 text-sm"
                              title="Delete Point"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">How Point Multipliers Work</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-2">7-Day Streak (1.2x)</h4>
              <p>All points earned are multiplied by 1.2 when you maintain a 7-day practice streak.</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-2">30-Day Streak (1.5x)</h4>
              <p>All points earned are multiplied by 1.5 when you maintain a 30-day practice streak.</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-2">100-Day Streak (2.0x)</h4>
              <p>All points earned are multiplied by 2.0 when you maintain a 100-day practice streak.</p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Points are automatically calculated and awarded based on your practice activities. 
            Check your progress on the leaderboard to see how you rank among your peers!
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPoints;
